import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useWorkspaceEnabled, useWorkspaceNavigate } from "@/features/workspaces";

export interface AppNavigateOptions {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  title?: string;
}

/**
 * Unified navigation hook that works in both workspace and normal modes.
 *
 * In workspace mode: navigates within the active tab.
 * In normal mode: navigates via TanStack Router.
 *
 * Usage:
 *   const navigate = useAppNavigate();
 *   navigate({ to: "/specPilot/$jobId", params: { jobId }, title: job.name });
 */
export function useAppNavigate() {
  const isWorkspacesEnabled = useWorkspaceEnabled();
  const workspaceNavigate = useWorkspaceNavigate();
  const routerNavigate = useNavigate();

  return useCallback(
    (options: AppNavigateOptions) => {
      if (isWorkspacesEnabled) {
        workspaceNavigate({
          route: options.to,
          params: options.search ? { ...options.params, ...options.search } : options.params,
          title: options.title,
        });
      } else {
        routerNavigate({
          to: options.to as "/",
          params: options.params,
          search: options.search,
        });
      }
    },
    [isWorkspacesEnabled, workspaceNavigate, routerNavigate]
  );
}
